import { keywordsFor } from "./keywords";


export const ORG = {
  name: "Vinsafe",
  parent: "Vingrace Solutions Private Limited",
  tagline: "One-stop fire safety supply and AMC partner for public spaces across India",
  phone: "+91 96543 99019",
  email: "shhrey@vingrace.com",
  street: "A-111, Lajpat Nagar 1",
  city: "New Delhi",
  region: "Delhi",
  postal: "110024",
};

const SCOPE_TEXT = {
  supply: "Vinsafe supplies, positions and wall-mounts it. No civil or plumbing work is involved.",
  wiring: "Vinsafe's electrical team handles the low-voltage installation: cabling, containment, device mounting and termination (wiring only).",
};

const list = (a) => (a.length > 1 ? `${a.slice(0, -1).join(", ")} and ${a[a.length - 1]}` : a[0] || "");
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const clip = (s, n = 158) => (s.length > n ? `${s.slice(0, n - 1).replace(/\s+\S*$/, "")}…` : s);
const specOf = (p, key) => p.specs.find(([k]) => k.toLowerCase().includes(key))?.[1];

export const productTitle = (p, c) => {
  const kw = cap(keywordsFor(p)[0]);
  const norm = (s) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ");
  const named = norm(p.name).includes(norm(kw));
  const options = named
    ? [`${p.name} Supplier in India | Vinsafe`, `${p.name} | Vinsafe India`, `${p.name} | Vinsafe`]
    : [`${kw}: ${p.name} | Vinsafe`, `${kw} Supplier in India | Vinsafe`, `${kw} | Vinsafe India`, `${kw} | ${p.name}`];
  return options.find((t) => t.length >= 30 && t.length <= 60) || clip(options[options.length - 1], 60);
};

export const productSummary = (p, c) => {
  const std = specOf(p, "standard")?.replace(/\s*referenced$/i, "");
  const parts = [std && `Referenced to ${std}.`, `Used in ${list(p.uses.slice(0, 2))}.`, "Bulk supply & AMC across India.", "Get a quote.", `Also for ${list(p.uses.slice(2, 4))}.`].filter(Boolean);
  let out = `${cap(keywordsFor(p)[0])} from Vinsafe: ${p.tagline}.`;
  for (const part of parts) if (`${out} ${part}`.length <= 158) out = `${out} ${part}`;
  return out.length >= 120 ? out : clip(`${out} Vinsafe, a part of Vingrace Solutions, supplies across India.`);
};

export const productStandards = (p, c) => p.standards ?? c.standards;
const scopeText = (p, c) => (p.models ? "Vinsafe supplies it under its own model codes, with handover documentation and after-sales support." : SCOPE_TEXT[c.scope]);

export const productDescription = (p, c) =>
  `Searching for ${keywordsFor(p)[0]} in India? ${p.name} from Vinsafe's ${c.name} range: ${p.tagline.toLowerCase()}. Key specifications: ${p.specs.map(([k, v]) => `${k} ${v}`).join("; ")}. Typical applications include ${list(p.uses)}, across ${list(c.industries.slice(0, 3))}. ${scopeText(p, c)} ${p.models ? `Available in ${p.models.length} Vinsafe model${p.models.length > 1 ? "s" : ""}: ${p.models.map((m) => `${m.code} (${m.name})`).join(", ")}. Pricing is shared on enquiry.` : "Makes and models are proposed per site requirement, and pricing is shared on enquiry."}`;

export const productFaqs = (p, c) => [
  ...(p.models ? [[`Which ${p.name} models does Vinsafe supply?`, p.models.map((m) => `${m.code}: ${m.name}, ${m.features}${m.load !== "—" ? `, load ${m.load}` : ""}${m.weight !== "—" ? `, weight ${m.weight}` : ""}`).join(". ") + "."]] : []),
  [`Where is the ${p.name} used?`, `Typical applications include ${list(p.uses)}. It is also deployed across ${list(c.industries.slice(0, 4))}.`],
  [`What are the specifications of the ${p.name}?`, `${p.specs.map(([k, v]) => `${k}: ${v}`).join(". ")}.`],
  ...(productStandards(p, c).length ? [[`Which standards apply to the ${p.name}?`, `Vinsafe references it to ${list(productStandards(p, c))}.`]] : []),
  [`Does Vinsafe install the ${p.name}?`, scopeText(p, c)],
  [`How do I get a price for the ${p.name}?`, "Tap Get a quote on the product page and fill the 2-minute form. A Vinsafe specialist replies with pricing based on quantity and site requirement, usually within 1 business day."],
  ...(keywordsFor(p).length > 1 ? [[`What ${/(&| and |s)$/i.test(p.name) || p.name.includes("&") ? "are" : "is"} the ${p.name} also known as?`, `Buyers also search for it as ${list(keywordsFor(p).slice(1))}.`]] : []),
  ...c.faqs,
];

const faqLd = (faqs) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});

const crumbs = (site, items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${site}${path}` })),
});

export const productJsonLd = (site, p, c) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Product",
      name: p.name,
      description: productDescription(p, c),
      image: [p.image],
      category: c.name,
      url: `${site}/products/${p.id}`,
      additionalProperty: p.specs.map(([k, v]) => ({ "@type": "PropertyValue", name: k, value: v })),
      ...(p.models ? { model: p.models.map((m) => ({ "@type": "ProductModel", name: `${p.name} ${m.code}`, sku: m.code, description: m.features })) } : {}),
      audience: { "@type": "BusinessAudience", audienceType: list(p.uses) },
      seller: { "@type": "Organization", name: ORG.name, telephone: ORG.phone, parentOrganization: { "@type": "Organization", name: ORG.parent } },
    },
    crumbs(site, [["Home", "/"], ["Solutions", "/solutions"], [c.name, `/solutions/${c.slug}`], [p.name, `/products/${p.id}`]]),
    faqLd(productFaqs(p, c)),
  ],
});

export const categoryTitle = (c) => `${c.name} supplier in India | Vinsafe fire safety`;
export const categorySummary = (c) => clip(`${c.short} ${c.products.length} product lines referenced to ${list(c.standards)}. Bulk supply and AMC by Vinsafe across India.`);

export const categoryJsonLd = (site, c) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: c.name,
      description: c.intro,
      url: `${site}/solutions/${c.slug}`,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: c.products.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, url: `${site}/products/${p.id}` })),
      },
    },
    crumbs(site, [["Home", "/"], ["Solutions", "/solutions"], [c.name, `/solutions/${c.slug}`]]),
    faqLd(c.faqs),
  ],
});

export const LLMS_INTRO = `${ORG.name} (a part of ${ORG.parent}): ${ORG.tagline}. Supply and wall-mounting of fire extinguishers, fire balls, fire and EV blankets, photoluminescent signage, stair evacuation chairs, tracked stretchers, evacuation sheets, baby evacuation suits and rescue stretchers, safety cabinets and firefighter PPE, plus low-voltage wiring of fire detection, fire alarm, voice evacuation and emergency lighting, and annual maintenance contracts (AMC).`;
